import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridDensity, GridRowParams } from '@mui/x-data-grid';

import { fetchEnrichedEvents } from './analyticsApi';
import type { EnrichedEventOut } from './analyticsTypes';
import { readAxiosDetail } from '../api_calls';
import { trackAnalyticsEvent } from '../../../services/analytics';
import EmptyState from './components/EmptyState';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';
import { useInspector } from './hooks/useInspector';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors } from '../../../theme/opsTokens';

const PAGE_SIZE = 50;
const SAVED_VIEWS_KEY = 'atlas_admin_analytics_saved_views';
const EXPORT_HARD_CAP = 5000;

type Row = EnrichedEventOut & { _rowId: string };

interface SavedView {
  id: string;
  name: string;
  query: string;
}

function isLegacy(name: string): boolean {
  return name.startsWith('legacy.');
}

function readSavedViews(): SavedView[] {
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedView[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v) => typeof v?.id === 'string' && typeof v?.name === 'string' && typeof v?.query === 'string');
  } catch {
    return [];
  }
}

function writeSavedViews(views: SavedView[]) {
  try {
    window.localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(views));
  } catch {
    // Storage unavailable; ignore.
  }
}

function jsonPreview(meta: Record<string, unknown> | null | undefined): string {
  if (!meta || Object.keys(meta).length === 0) return '—';
  const json = JSON.stringify(meta);
  return json.length > 80 ? json.slice(0, 77) + '…' : json;
}

function csvEscape(value: unknown): string {
  if (value == null) return '';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows: EnrichedEventOut[]): string {
  const header = [
    'event_created_at',
    'received_event_name',
    'category',
    'username',
    'customer_name',
    'screen',
    'session_id',
    'meta',
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push([
      csvEscape(r.event_created_at),
      csvEscape(r.received_event_name),
      csvEscape(r.category),
      csvEscape(r.username),
      csvEscape(r.customer_name),
      csvEscape(r.screen),
      csvEscape(r.session_id),
      csvEscape(JSON.stringify(r.meta ?? {})),
    ].join(','));
  }
  return lines.join('\n');
}

export default function EventsBrowser() {
  const { filters, setFilters } = useAnalyticsFilters();
  const inspector = useInspector();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Drop low-value columns at narrower viewports so the table never needs to scroll.
  const showPayload = useMediaQuery('(min-width:1200px)');
  const showSession = useMediaQuery('(min-width:1400px)');

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [density, setDensity] = useState<GridDensity>('compact');
  const [draftEventName, setDraftEventName] = useState(filters.eventName ?? '');
  const [draftCategory, setDraftCategory] = useState(filters.category ?? '');
  const [draftCustomer, setDraftCustomer] = useState(filters.customerId ?? '');
  const [draftActor, setDraftActor] = useState(filters.actorId ?? '');
  const [draftScreen, setDraftScreen] = useState(filters.screen ?? '');
  const [draftActorType, setDraftActorType] = useState(filters.actorType ?? '');
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => readSavedViews());
  const [savedSelection, setSavedSelection] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Sync drafts when URL filters change externally (e.g. from a chart click).
  useEffect(() => {
    setDraftEventName(filters.eventName ?? '');
    setDraftCategory(filters.category ?? '');
    setDraftCustomer(filters.customerId ?? '');
    setDraftActor(filters.actorId ?? '');
    setDraftScreen(filters.screen ?? '');
    setDraftActorType(filters.actorType ?? '');
  }, [filters.eventName, filters.category, filters.customerId, filters.actorId, filters.screen, filters.actorType]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEnrichedEvents({
        event_name: filters.eventName,
        category: filters.category,
        customer_id: filters.customerId,
        actor_id: filters.actorId,
        screen: filters.screen,
        actor_type: filters.actorType,
        since: filters.since,
        until: filters.until,
        page: page + 1,
        page_size: PAGE_SIZE,
      });
      const items = filters.hideLegacy
        ? res.items.filter((i) => !isLegacy(i.received_event_name))
        : res.items;
      setRows(items.map((item, i) => ({ ...item, _rowId: `${page}-${i}` })));
      setTotal(res.total);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setLoading(false);
    }
  }, [
    filters.eventName,
    filters.category,
    filters.customerId,
    filters.actorId,
    filters.screen,
    filters.actorType,
    filters.since,
    filters.until,
    filters.hideLegacy,
    page,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset to page 0 when filters change (not page changes).
  useEffect(() => {
    setPage(0);
  }, [
    filters.eventName,
    filters.category,
    filters.customerId,
    filters.actorId,
    filters.screen,
    filters.actorType,
    filters.since,
    filters.until,
    filters.hideLegacy,
  ]);

  const trackFilter = (dimension: 'event_name' | 'category' | 'customer_id' | 'actor_id' | 'screen' | 'actor_type' | 'hide_legacy') => {
    trackAnalyticsEvent('admin_analytics_filter_applied', {
      filter_dimension: dimension,
      active_view: 'events',
      timestamp: new Date().toISOString(),
    });
  };

  const applyDrafts = () => {
    if (draftEventName !== (filters.eventName ?? '')) trackFilter('event_name');
    if (draftCategory !== (filters.category ?? '')) trackFilter('category');
    if (draftCustomer !== (filters.customerId ?? '')) trackFilter('customer_id');
    if (draftActor !== (filters.actorId ?? '')) trackFilter('actor_id');
    if (draftScreen !== (filters.screen ?? '')) trackFilter('screen');
    if (draftActorType !== (filters.actorType ?? '')) trackFilter('actor_type');
    setFilters({
      eventName: draftEventName || null,
      category: draftCategory || null,
      customerId: draftCustomer || null,
      actorId: draftActor || null,
      screen: draftScreen || null,
      actorType: draftActorType || null,
    });
  };

  const clearDrafts = () => {
    setDraftEventName('');
    setDraftCategory('');
    setDraftCustomer('');
    setDraftActor('');
    setDraftScreen('');
    setDraftActorType('');
    setFilters({ eventName: null, category: null, customerId: null, actorId: null, screen: null, actorType: null });
  };

  const saveCurrentView = () => {
    const name = window.prompt('Name this view:');
    if (!name) return;
    const query = window.location.search;
    const view: SavedView = { id: `${Date.now()}`, name, query };
    const next = [...savedViews.filter((v) => v.name !== name), view];
    setSavedViews(next);
    writeSavedViews(next);
    setSavedSelection(view.id);
  };

  const applySavedView = (id: string) => {
    setSavedSelection(id);
    const view = savedViews.find((v) => v.id === id);
    if (!view) return;
    const params = new URLSearchParams(view.query);
    params.set('view', 'events');
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
    trackAnalyticsEvent('admin_analytics_saved_view_applied', {
      has_event_name_filter: params.has('eventName'),
      has_category_filter: params.has('category'),
      has_customer_filter: params.has('customerId'),
      timestamp: new Date().toISOString(),
    });
  };

  const deleteSavedView = () => {
    if (!savedSelection) return;
    const next = savedViews.filter((v) => v.id !== savedSelection);
    setSavedViews(next);
    writeSavedViews(next);
    setSavedSelection('');
  };

  const copyShareLink = async () => {
    const url = window.location.href;
    const params = new URLSearchParams(window.location.search);
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', url);
    }
    trackAnalyticsEvent('admin_analytics_share_link_copied', {
      active_view: 'events',
      param_count: Array.from(params.keys()).length,
      has_event_name_filter: params.has('eventName'),
      has_category_filter: params.has('category'),
      has_customer_filter: params.has('customerId'),
      has_date_range: params.has('since') || params.has('until') || params.has('preset'),
      timestamp: new Date().toISOString(),
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    trackAnalyticsEvent('admin_analytics_export_clicked', {
      format: 'csv',
      filtered: !!(filters.eventName || filters.category || filters.customerId || filters.since || filters.until),
      timestamp: new Date().toISOString(),
    });
    try {
      const collected: EnrichedEventOut[] = [...rows];
      let nextPage = page + 2;
      while (collected.length < EXPORT_HARD_CAP) {
        const res = await fetchEnrichedEvents({
          event_name: filters.eventName,
          category: filters.category,
          customer_id: filters.customerId,
          since: filters.since,
          until: filters.until,
          page: nextPage,
          page_size: PAGE_SIZE,
        });
        const items = filters.hideLegacy
          ? res.items.filter((i) => !isLegacy(i.received_event_name))
          : res.items;
        if (items.length === 0) break;
        collected.push(...items);
        nextPage += 1;
        if (res.items.length < PAGE_SIZE) break;
      }
      const capped = collected.slice(0, EXPORT_HARD_CAP);
      const csv = rowsToCsv(capped);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-events-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<GridColDef<Row>[]>(() => [
    {
      field: 'event_created_at',
      headerName: 'Time',
      minWidth: 140,
      flex: 1,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'received_event_name',
      headerName: 'Event',
      minWidth: 180,
      flex: 1.4,
      renderCell: (params) => {
        const name = params.value as string;
        const legacy = isLegacy(name);
        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: 12, color: legacy ? 'text.disabled' : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {name}
            </Typography>
            {legacy && <Chip label="Legacy" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
        );
      },
    },
    { field: 'category', headerName: 'Category', minWidth: 90, flex: 0.6 },
    { field: 'username', headerName: 'User', minWidth: 110, flex: 0.9 },
    { field: 'customer_name', headerName: 'Customer', minWidth: 110, flex: 0.8 },
    { field: 'screen', headerName: 'Screen', minWidth: 90, flex: 0.7 },
    { field: 'session_id', headerName: 'Session', minWidth: 100, flex: 0.7 },
    {
      field: 'meta',
      headerName: 'Payload',
      minWidth: 160,
      flex: 1.2,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={JSON.stringify(params.value ?? {}, null, 2)} arrow placement="left">
          <Typography variant="caption" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {jsonPreview(params.value as Record<string, unknown>)}
          </Typography>
        </Tooltip>
      ),
    },
  ], []);

  const columnVisibility = useMemo(() => ({
    session_id: showSession,
    meta: showPayload,
  }), [showSession, showPayload]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        flexWrap={{ md: 'wrap' }}
        useFlexGap
      >
        <TextField
          label="Event name"
          size="small"
          value={draftEventName}
          onChange={(e) => setDraftEventName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 200 } }}
        />
        <TextField
          label="Category"
          size="small"
          value={draftCategory}
          onChange={(e) => setDraftCategory(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 140 } }}
        />
        <TextField
          label="Customer ID"
          size="small"
          value={draftCustomer}
          onChange={(e) => setDraftCustomer(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 160 } }}
        />
        <TextField
          label="Actor ID"
          size="small"
          value={draftActor}
          onChange={(e) => setDraftActor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 140 } }}
        />
        <TextField
          label="Screen"
          size="small"
          value={draftScreen}
          onChange={(e) => setDraftScreen(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 140 } }}
        />
        <TextField
          label="Actor type"
          size="small"
          value={draftActorType}
          onChange={(e) => setDraftActorType(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyDrafts(); }}
          sx={{ minWidth: { xs: '100%', md: 130 } }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" size="small" onClick={applyDrafts}>
            Apply
          </Button>
          <Button size="small" onClick={clearDrafts}>
            Clear
          </Button>
        </Stack>
        <Box sx={{ flex: { md: 1 } }} />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.hideLegacy}
                onChange={(_, c) => {
                  trackFilter('hide_legacy');
                  setFilters({ hideLegacy: c });
                }}
              />
            }
            label={<Typography variant="caption">Hide legacy</Typography>}
          />
          <Tooltip title={density === 'compact' ? 'Switch to comfortable density' : 'Switch to compact density'} arrow>
            <IconButton size="small" onClick={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}>
              {density === 'compact' ? <Icon d={ICON_FALLBACKS.ViewAgenda} size={20} /> : <Icon d={ICON_FALLBACKS.ViewCompact} size={20} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={`Export current view to CSV (up to ${EXPORT_HARD_CAP.toLocaleString()} rows)`} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon name="file-arrow-down" size={20} />}
                onClick={() => void exportCsv()}
                disabled={exporting || total === 0}
              >
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Saved views row */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">Saved views:</Typography>
        <Select
          size="small"
          value={savedSelection}
          onChange={(e) => applySavedView(e.target.value)}
          displayEmpty
          sx={{ minWidth: { xs: 160, sm: 200 }, flex: { xs: 1, sm: 'none' } }}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {savedViews.map((v) => (
            <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
          ))}
        </Select>
        <Tooltip title="Save the current filters as a view" arrow>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon d={ICON_FALLBACKS.BookmarkAdd} size={20} />}
            onClick={saveCurrentView}
          >
            Save
          </Button>
        </Tooltip>
        <Tooltip title={shareCopied ? 'Link copied to clipboard' : 'Copy a link to this filtered view'} arrow>
          <Button
            size="small"
            variant="outlined"
            color={shareCopied ? 'success' : 'primary'}
            startIcon={shareCopied ? <Icon name="check" size={20} /> : <Icon name="link" size={20} />}
            onClick={() => void copyShareLink()}
          >
            {shareCopied ? 'Copied' : 'Share'}
          </Button>
        </Tooltip>
        {savedSelection && (
          <Tooltip title="Delete the selected saved view" arrow>
            <Button
              size="small"
              variant="text"
              color="error"
              startIcon={<Icon d={ICON_FALLBACKS.BookmarkRemove} size={20} />}
              onClick={deleteSavedView}
            >
              Remove
            </Button>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {total.toLocaleString()} matching event{total === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {isMobile ? (
        <Box>
          {!loading && rows.length === 0 ? (
            <EmptyState
              title="No events match the current filters"
              body="Try widening the date range, removing filters, or toggling legacy events."
              actionLabel="Clear filters"
              onAction={clearDrafts}
            />
          ) : (
            <Stack divider={<Divider />} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {rows.map((row) => {
                const legacy = isLegacy(row.received_event_name);
                const meta = row.meta as Record<string, unknown> | null | undefined;
                const metaPreview = meta && Object.keys(meta).length > 0 ? jsonPreview(meta) : null;
                return (
                  <Box
                    key={row._rowId}
                    onClick={() => inspector.open({ kind: 'event', event: row })}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        inspector.open({ kind: 'event', event: row });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Inspect event ${row.received_event_name}`}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&:focus-visible': { outline: `2px solid ${opsColors.brand.primary}`, outlineOffset: -2 },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: legacy ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {row.received_event_name}
                      </Typography>
                      {legacy && (
                        <Chip label="Legacy" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {new Date(row.event_created_at).toLocaleTimeString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: metaPreview ? 0.5 : 0 }}>
                      {row.category && <Chip label={row.category} size="small" variant="outlined" sx={{ height: 20 }} />}
                      {row.username && (
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.username}
                        </Typography>
                      )}
                      {row.customer_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          · {row.customer_name}
                        </Typography>
                      )}
                      {row.screen && (
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          · {row.screen}
                        </Typography>
                      )}
                    </Stack>
                    {metaPreview && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {metaPreview}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                size="small"
                siblingCount={0}
                boundaryCount={1}
              />
            </Stack>
          )}
        </Box>
      ) : (
        <Box sx={{ height: 600, minWidth: 0, width: '100%' }}>
          {!loading && rows.length === 0 ? (
            <EmptyState
              title="No events match the current filters"
              body="Try widening the date range, removing filters, or toggling legacy events."
              actionLabel="Clear filters"
              onAction={clearDrafts}
            />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisibility}
              getRowId={(row) => row._rowId}
              rowCount={total}
              paginationMode="server"
              paginationModel={{ page, pageSize: PAGE_SIZE }}
              onPaginationModelChange={(model) => setPage(model.page)}
              pageSizeOptions={[PAGE_SIZE]}
              loading={loading}
              disableRowSelectionOnClick
              density={density}
              onRowClick={(params: GridRowParams<Row>) => {
                inspector.open({ kind: 'event', event: params.row });
              }}
              sx={{
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      )}
    </Stack>
  );
}
